import React, { useContext, useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select
} from "@material-ui/core";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexWrap: "wrap"
  },
  textField: {
    marginRight: theme.spacing(1),
    flex: 1
  },
  btnWrapper: {
    position: "relative"
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12
  },
  formControl: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    minWidth: 120
  }
}));

const ExtensionSchema = Yup.object().shape({
  extension: Yup.string()
    .matches(/^[0-9]{2,10}$/, i18n.t("extensionModal.form.extensionValidation"))
    .required("Required"),
  label: Yup.string().max(60, "Too Long!")
});

const ExtensionModal = ({ open, onClose, extensionId, companyId }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const initialState = {
    extension: "",
    label: "",
    userId: "",
    isActive: true
  };

  const [extension, setExtension] = useState(initialState);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const { data } = await api.get("/users", {
          params: { transfer: true, pageNumber: 1 }
        });
        setUsers(data.users || []);
      } catch (err) {
        toastError(err);
      }
    };

    loadUsers();
  }, []);

  useEffect(() => {
    (async () => {
      if (!extensionId) return;
      try {
        const { data } = await api.get(`/extensions/${extensionId}`);
        setExtension({
          extension: data.extension || "",
          label: data.label || "",
          userId: data.userId || "",
          isActive: data.isActive !== false
        });
      } catch (err) {
        toastError(err);
      }
    })();

    return () => {
      setExtension(initialState);
    };
  }, [extensionId, open]);

  const handleClose = () => {
    onClose();
    setExtension(initialState);
  };

  const handleSave = async values => {
    const payload = {
      extension: values.extension,
      label: values.label || null,
      userId: values.userId ? Number(values.userId) : null,
      isActive: Boolean(values.isActive)
    };

    try {
      if (extensionId) {
        await api.put(`/extensions/${extensionId}`, payload);
      } else {
        await api.post("/extensions", payload);
      }
      toast.success(i18n.t("extensionModal.success"));
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  const buildZoiperUser = extensionValue => {
    const normalizedCompanyId = String(companyId || user?.companyId || "").replace(/\D/g, "");
    const normalizedExtension = String(extensionValue || "").replace(/[^0-9A-Za-z.-]/g, "");

    if (!normalizedCompanyId || !normalizedExtension) {
      return "";
    }

    return `c${normalizedCompanyId}-${normalizedExtension}`;
  };

  return (
    <div className={classes.root}>
      <Dialog open={open} onClose={handleClose} scroll="paper" maxWidth="sm" fullWidth>
        <DialogTitle>
          {extensionId
            ? i18n.t("extensionModal.title.edit")
            : i18n.t("extensionModal.title.add")}
        </DialogTitle>
        <Formik
          initialValues={extension}
          enableReinitialize
          validationSchema={ExtensionSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSave(values);
              actions.setSubmitting(false);
            }, 300);
          }}
        >
          {({ touched, errors, isSubmitting, values, setFieldValue }) => (
            <Form>
              <DialogContent dividers>
                <Field
                  as={TextField}
                  label={i18n.t("extensionModal.form.extension")}
                  autoFocus
                  name="extension"
                  error={touched.extension && Boolean(errors.extension)}
                  helperText={
                    (touched.extension && errors.extension) ||
                    i18n.t("extensionModal.form.zoiperUserHint", {
                      defaultValue: "Login Zoiper: {{value}}",
                      value: buildZoiperUser(values.extension) || "-"
                    })
                  }
                  variant="outlined"
                  margin="dense"
                  className={classes.textField}
                  fullWidth
                />
                <Field
                  as={TextField}
                  label={i18n.t("extensionModal.form.label")}
                  name="label"
                  error={touched.label && Boolean(errors.label)}
                  helperText={touched.label && errors.label}
                  variant="outlined"
                  margin="dense"
                  className={classes.textField}
                  fullWidth
                />
                <FormControl variant="outlined" margin="dense" fullWidth className={classes.formControl}>
                  <InputLabel id="extension-user-label">
                    {i18n.t("extensionModal.form.user")}
                  </InputLabel>
                  <Field
                    as={Select}
                    labelId="extension-user-label"
                    label={i18n.t("extensionModal.form.user")}
                    name="userId"
                    value={values.userId}
                    onChange={event => setFieldValue("userId", event.target.value)}
                  >
                    <MenuItem value="">
                      {i18n.t("extensionModal.form.unassigned")}
                    </MenuItem>
                    {users.map(user => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.name}
                      </MenuItem>
                    ))}
                  </Field>
                </FormControl>
                <FormControl variant="outlined" margin="dense" fullWidth className={classes.formControl}>
                  <InputLabel id="extension-active-label">
                    {i18n.t("extensionModal.form.status")}
                  </InputLabel>
                  <Field
                    as={Select}
                    labelId="extension-active-label"
                    label={i18n.t("extensionModal.form.status")}
                    name="isActive"
                    value={values.isActive}
                    onChange={event => setFieldValue("isActive", event.target.value)}
                  >
                    <MenuItem value>{i18n.t("extensionModal.form.active")}</MenuItem>
                    <MenuItem value={false}>{i18n.t("extensionModal.form.inactive")}</MenuItem>
                  </Field>
                </FormControl>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleClose} color="secondary" disabled={isSubmitting} variant="outlined">
                  {i18n.t("extensionModal.buttons.cancel")}
                </Button>
                <Button type="submit" color="primary" disabled={isSubmitting} variant="contained" className={classes.btnWrapper}>
                  {extensionId
                    ? i18n.t("extensionModal.buttons.okEdit")
                    : i18n.t("extensionModal.buttons.okAdd")}
                  {isSubmitting && (
                    <CircularProgress size={24} className={classes.buttonProgress} />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default ExtensionModal;
